import { money } from "../lib/format";
import { t } from "../lib/sprache";
import { planName, tarifName, type TarifKatalog } from "./tarif";

/**
 * Texte zur Testphase und zum Testende nach den Vorschlägen des Vertriebs (0323, Praxistest 15.10.2026). Reine Logik ohne React.
 *
 * Grundsatz: Fehlt nur die Zahlungsart, ist das kein „Upgrade“: genannt wird der gewählte Tarif mit Preis. „Upgrade“ nur, wenn
 * eine im Test genutzte Funktion im gewählten Tarif fehlt. Empfohlen ist der kleinste Tarif, der alles im Test Genutzte abdeckt
 * (public.trial_scope.recommended, aus app.trial_recommendation); reicht der gewählte, dann der gewählte. Preise und Namen kommen
 * aus dem Tarifkatalog (plans), nie fest im Code. Kein Druck, keine Rabatte; Kündigen und Löschen bleiben in den Einstellungen.
 */

export type Intervall = "month" | "year";

/** Tarif mit Anzeigename und Preis im Intervall des Abos (Cent, netto); Preis null, wenn der Katalog ihn nicht nennt */
export interface TarifMitPreis {
  key: string;
  name: string;
  cents: number | null;
  interval: Intervall;
}

export function intervallVon(v: string | null | undefined): Intervall {
  return v === "year" ? "year" : "month";
}

/** Tarif aus dem Katalog mit Preis im gewünschten Intervall; ohne Katalogeintrag nur der Name aus der festen Zuordnung. */
export function tarifMitPreis(key: string | null | undefined, katalog: TarifKatalog | null | undefined, interval: Intervall): TarifMitPreis | null {
  if (!key) return null;
  const p = katalog?.plans.find((x) => x.key === key);
  const cents = p ? (interval === "year" ? p.price_year_cents : p.price_month_cents) : null;
  return { key, name: p ? planName(p) : tarifName(key), cents: typeof cents === "number" ? cents : null, interval };
}

/** „39,00 € netto im Monat“ bzw. „… im Jahr“ */
export function preisSatz(cents: number, interval: Intervall): string {
  // Inhaber 21.10.2026: überall der Monatsbetrag, bei jährlicher Abrechnung mit dem Zusatz (keine Jahressumme als Hauptzahl)
  return interval === "year" ? t(`${money(Math.round(cents / 12), "EUR")} netto im Monat (jährlich abgerechnet)`, `${money(Math.round(cents / 12), "EUR")} net per month (billed annually)`)
    : t(`${money(cents, "EUR")} netto im Monat`, `${money(cents, "EUR")} net per month`);
}

/** „Solo für 39,00 € netto im Monat“; ohne Preis nur der Name */
export function tarifUndPreis(p: TarifMitPreis): string {
  return p.cents === null ? p.name : t(`${p.name} für ${preisSatz(p.cents, p.interval)}`, `${p.name} for ${preisSatz(p.cents, p.interval)}`);
}

/**
 * F (K-479): Resttage nach dem heutigen Tag, passend zu „Tag n von 14“: noch 14 − n Tage; am letzten Tag „letzter Tag“
 * (das Ende kann auch am frühen Morgen des Folgetags liegen, „endet heute“ wäre dann falsch).
 */
export function restTageText(noch: number): string {
  return noch <= 0 ? t("letzter Tag", "last day") : noch === 1 ? t("noch 1 Tag", "1 day left") : t(`noch ${noch} Tage`, `${noch} days left`);
}
export function restTageKlammer(noch: number): string {
  return ` (${restTageText(noch)})`;
}

/** Satzende nach einem Datum: „bis 17.10.“ bekommt keinen zweiten Punkt, „bis 17/10“ schon */
function punkt(s: string): string {
  return s.endsWith(".") ? s : `${s}.`;
}

/** Erste Abbuchung erst zum Testende: nur, wenn beim Abschluss noch mehr als 49 Stunden Test bleiben (wie Abo.tsx/Stripe) */
export function abbuchungErstZumTestende(endsAt: string, nowMs: number): boolean {
  return Date.parse(endsAt) - nowMs > 49 * 3_600_000;
}

/**
 * 1 und 2: Leiste im Test. Ohne Zahlungsart der Weg „Zahlungsart hinterlegen“ (gewählter Tarif vorgewählt); mit Zahlungsart, was
 * danach weiterläuft, und nur wenn im Test mehr genutzt wurde, der Hinweis auf den empfohlenen Tarif.
 */
export function testLeiste(o: {
  bis: string; tage: number | null; vollerUmfang: boolean; mitZahlungsart: boolean; abbuchungSpaeter: boolean;
  gewaehlt: TarifMitPreis | null; empfohlen: TarifMitPreis | null;
  /** E (K-484): Test mit Zahlungsart, „Nach dem Test automatisch weiterlaufen“ ausgeschaltet: der Test endet ohne Kosten */
  endetOhneKosten?: boolean;
}): { titel: string; tage: string; text: string; hinweis: string | null } {
  const titel = o.vollerUmfang ? t(`Test im vollen Umfang bis ${o.bis}`, `Full-scope trial until ${o.bis}`) : t(`Test bis ${o.bis}`, `Trial until ${o.bis}`);
  const tage = o.tage === null ? "" : restTageKlammer(o.tage);
  const g = o.gewaehlt;
  if (!o.mitZahlungsart) {
    // K-491: Tarif und Preis nennen, die mit Zahlungsart danach gelten; K-575: ohne Zahlungsart endet der Test ohne Kosten
    const weiter = g && g.cents !== null
      ? t(`Ohne Zahlungsart endet er dann ohne Kosten. Mit Zahlungsart läuft dein Tarif ${g.name} für ${preisSatz(g.cents, g.interval)} weiter`,
        `Without a payment method it then ends at no cost. With a payment method your ${g.name} plan continues for ${preisSatz(g.cents, g.interval)}`)
      : null;
    return {
      titel, tage,
      text: weiter
        ? weiter + (o.abbuchungSpaeter
          ? t(`, abgebucht wird erst ab ${punkt(o.bis)}`, `, nothing is charged before ${punkt(o.bis)}`)
          : t(" ohne Unterbrechung.", " without interruption."))
        : t("Hinterlege schon jetzt eine Zahlungsart, dann geht es danach ohne Unterbrechung weiter.", "Add a payment method now and you will carry on without interruption afterwards.")
          + (o.abbuchungSpaeter ? t(` Abgebucht wird erst ab ${punkt(o.bis)}`, ` Nothing is charged before ${punkt(o.bis)}`) : ""),
      hinweis: null,
    };
  }
  if (o.endetOhneKosten) {
    return { titel, tage, text: t("Danach endet er ohne Kosten, automatisch weiterlaufen ist ausgeschaltet.", "Afterwards it ends at no cost; automatic renewal is switched off."), hinweis: null };
  }
  const text = !g ? t("Danach läuft dein Tarif weiter.", "Afterwards your plan continues.")
    : g.cents === null ? t(`Danach läuft dein Tarif ${g.name} weiter.`, `Afterwards your ${g.name} plan continues.`)
    : t(`Danach läuft dein Tarif ${g.name} für ${preisSatz(g.cents, g.interval)} weiter.`, `Afterwards your ${g.name} plan continues for ${preisSatz(g.cents, g.interval)}.`);
  const mehr = !!o.empfohlen && !!g && o.empfohlen.key !== g.key;
  return {
    titel, tage, text,
    hinweis: mehr ? t(`Im Test nutzt du Funktionen aus ${o.empfohlen!.name}. Damit sie bleiben, wechsle vor dem ${punkt(o.bis)}`, `During the trial you use features from ${o.empfohlen!.name}. To keep them, switch before ${punkt(o.bis)}`) : null,
  };
}

/** Ende der Export-Phase ohne Tarif (org_lifecycle_status.plan: nächster Schritt Reduktion, sonst Löschung); 0425: Portal gesperrt, Export geht */
export function lesbarBis(plan: { next_action?: "reduce" | "purge" | "hold"; next_at?: string; purge_at?: string } | null | undefined): string | null {
  if (!plan) return null;
  if (plan.next_action === "reduce" && plan.next_at) return plan.next_at;
  return plan.purge_at ?? plan.next_at ?? null;
}

/** 3: Kasten nach dem Testende ohne Zahlungsart */
export function testendeKasten(o: { empfohlen: TarifMitPreis | null; bis: string | null }): { titel: string; text: string; weiter: string | null; klein: string | null } {
  const e = o.empfohlen;
  return {
    titel: t("Dein Test ist vorbei, deine Daten sind sicher.", "Your trial is over, your data is safe."),
    text: t("Alles, was du angelegt hast, bleibt erhalten; exportieren kannst du es jederzeit.", "Everything you created is kept; you can export it at any time.") + " "
      + (e ? t(`Mit ${tarifUndPreis(e)} arbeitest du sofort weiter, genau da, wo du aufgehört hast.`, `With ${tarifUndPreis(e)} you carry on right away, exactly where you left off.`)
        : t("Mit einem Tarif arbeitest du sofort weiter, genau da, wo du aufgehört hast.", "With a plan you carry on right away, exactly where you left off.")),
    weiter: e ? weiterText(e.name) : null,
    klein: o.bis ? t(`Ohne Tarif kannst du deine Daten bis ${o.bis} exportieren.`, `Without a plan you can export your data until ${o.bis}.`) : null,
  };
}

/** „Mit Team weitermachen“ */
export function weiterText(name: string): string {
  return t(`Mit ${name} weitermachen`, `Continue with ${name}`);
}

/** 4: Neu anlegen nach dem Testende ohne Zahlungsart */
export function neuAnlegenNachTestText(): string {
  return t("Neu anlegen geht wieder, sobald dein Tarif läuft. Lesen und Export funktionieren weiter.", "Creating new entries works again as soon as your plan is running. Reading and export keep working.");
}

/**
 * 5: Fenster nach dem Testende mit Zahlungsart, wenn im Test etwas außerhalb des Tarifs genutzt wurde (sonst kein Fenster).
 * Differenz im Intervall des Abos; ohne Empfehlung (nichts deckt alles ab) nur der Satz zu den Daten.
 */
export function nachTestFenster(o: { gewaehlt: TarifMitPreis; empfohlen: TarifMitPreis | null }): { titel: string; einleitung: string; schluss: string; behalten: string | null; weiter: string } {
  const g = o.gewaehlt, e = o.empfohlen && o.empfohlen.key !== g.key ? o.empfohlen : null;
  const diff = e && e.cents !== null && g.cents !== null && e.cents > g.cents ? e.cents - g.cents : null;
  const daten = t("Deine Daten bleiben in jedem Fall erhalten.", "Your data is kept in any case.");
  return {
    titel: t(`Dein Test ist vorbei. Weiter mit ${g.name}.`, `Your trial is over. Continuing with ${g.name}.`),
    einleitung: t(`Im Test hast du diese Funktionen genutzt, die in ${g.name} nicht enthalten sind:`, `During the trial you used these features that are not included in ${g.name}:`),
    schluss: e && diff !== null
      ? t(`Mit ${e.name} behältst du alles, für ${money(diff, "EUR")} mehr im ${g.interval === "year" ? "Jahr" : "Monat"}. ${daten}`,
        `With ${e.name} you keep everything, for ${money(diff, "EUR")} more per ${g.interval === "year" ? "year" : "month"}. ${daten}`)
      : e ? t(`Mit ${e.name} behältst du alles. ${daten}`, `With ${e.name} you keep everything. ${daten}`) : daten,
    behalten: e ? t(`${e.name} behalten`, `Keep ${e.name}`) : null,
    weiter: t(`Mit ${g.name} weiter`, `Continue with ${g.name}`),
  };
}

/**
 * K-497 i: Mehrpreis am Hinweis einer gesperrten Funktion gegenüber dem eigenen Abo: „ · 90,00 € netto mehr im Monat“. Ohne
 * bekannten eigenen Preis (oder wenn der Zieltarif nicht teurer ist) der Preis des Zieltarifs wie bisher.
 */
export function mehrpreisZusatz(ziel: TarifMitPreis | null, eigen: TarifMitPreis | null): string {
  if (ziel && eigen && ziel.cents !== null && eigen.cents !== null && ziel.interval === eigen.interval && ziel.cents > eigen.cents) {
    const d = money(ziel.cents - eigen.cents, "EUR");
    return ziel.interval === "year" ? t(` · ${d} netto mehr im Jahr`, ` · ${d} net more per year`) : t(` · ${d} netto mehr im Monat`, ` · ${d} net more per month`);
  }
  return preisZusatz(ziel);
}

/** 6: Preis am Hinweis einer gesperrten Funktion: „Im Tarif Team enthalten · 129,00 € netto im Monat“ */
export function preisZusatz(p: TarifMitPreis | null): string {
  return p && p.cents !== null ? ` · ${preisSatz(p.cents, p.interval)}` : "";
}

/**
 * K-490: Text bei voller Grenze (Creator bzw. Team-Plätze) mit beiden Wegen und echten Preisen aus plans und plan_addons:
 * „Grenze erreicht. Weiter wachsen: 1 weiterer Creator für 6,00 € netto im Monat oder Upgrade auf Team für 129,00 € netto im
 * Monat (20 Creator, 3 Plätze).“ Fehlt ein Weg (kein Zusatzpaket, kein größerer Tarif), steht nur der andere da.
 */
export function grenzeWachsenText(o: {
  art: "talents" | "seats"; zusatzCents: number | null;
  ziel: { name: string; cents: number | null; talents: number | null; seats: number | null } | null;
}): string {
  const teile: string[] = [];
  if (o.zusatzCents !== null) {
    const preis = preisSatz(o.zusatzCents, "month");
    teile.push(o.art === "talents" ? t(`1 weiterer Creator für ${preis}`, `1 more creator for ${preis}`) : t(`1 weiterer Team-Platz für ${preis}`, `1 more team seat for ${preis}`));
  }
  if (o.ziel) {
    const z = o.ziel;
    const umfang = [
      z.talents === null ? null : z.talents < 0 ? t("unbegrenzt viele Creator", "unlimited creators") : t(`${z.talents} Creator`, `${z.talents} creators`),
      z.seats === null ? null : z.seats < 0 ? t("unbegrenzt viele Plätze", "unlimited seats") : z.seats === 1 ? t("1 Platz", "1 seat") : t(`${z.seats} Plätze`, `${z.seats} seats`),
    ].filter(Boolean).join(", ");
    teile.push(`${t(`Upgrade auf ${z.name}`, `upgrade to ${z.name}`)}${z.cents !== null ? t(` für ${preisSatz(z.cents, "month")}`, ` for ${preisSatz(z.cents, "month")}`) : ""}${umfang ? ` (${umfang})` : ""}`);
  }
  if (!teile.length) return t("Grenze erreicht.", "Limit reached.");
  return t(`Grenze erreicht. Weiter wachsen: ${teile.join(" oder ")}.`, `Limit reached. Keep growing: ${teile.join(" or ")}.`);
}
